import React from "react";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { showToast } from "src/app/methods/showToast";
import * as actionType from "./creatorSearchActions";
import CONSTS from "../../../helpers/consts";
import {
  getColumnOverridesFromStorage,
  saveColumnOverridesToStorage,
  getColumnOrderFromStorage,
  saveColumnOrderToStorage,
} from "./creatorSearchColumnsStorage";

// Keeps the "name" column pinned first, orders the rest by `order` (an array
// of column keys), and appends any columns missing from `order` at the end.
function reorderColumns(columns, order) {
  const nameColumn = columns.find((column) => column.key === "name");
  const reorderableColumns = columns.filter((column) => column.key !== "name");

  const orderedColumns = order
    .map((key) => reorderableColumns.find((column) => column.key === key))
    .filter(Boolean);
  const remainingColumns = reorderableColumns.filter(
    (column) => !order.includes(column.key),
  );

  const sortedColumns = [...orderedColumns, ...remainingColumns];
  return nameColumn ? [nameColumn, ...sortedColumns] : sortedColumns;
}

// A column is hidden unless the user has explicitly overridden it; the
// backend's `visible` flag only supplies the default for untouched columns.
function computeHiddenColumns(columns, columnOverrides) {
  return columns
    .filter((column) => {
      const isVisible = columnOverrides[column.key] ?? column.visible !== false;
      return !isVisible;
    })
    .map((column) => column.key);
}

const initialState = {
  interests: [],
  audienceCountries: [],
  audienceLanguages: [],
  creatorTags: null,
  audienceBrandAffinity: [],
  influencerCountries: [],
  influencerCountriesByProvider: [],
  keywords: [],
  unifiedCategories: [],

  searchAudienceFilters: {},

  searchAudienceResult: [],
  similarProfiles: [],
  searchAudienceInfo: {},
  lastFetchWasEmpty: false,
  isResultsLimitReached: false,

  selectedInfluencersOnResults: [],
  selectedInfluencersOnResultsUrls: [],

  velocitySummary: {},
  audienceLoading: false,

  pendingUsers: null,

  hasAccessToInfluencerInsightsToolVelocityIndex: false,

  isAudienceDataError: false,
  isAnalyzingBrief: false,

  columns: [],
  hiddenColumns: [],
  columnOverrides: getColumnOverridesFromStorage(),
  sortOptions: [],
};

// eslint-disable-next-line @typescript-eslint/default-param-last
export default function creatorSearchReducer(state = initialState, action) {
  switch (action.type) {
    case actionType.CS_GET_SETTINGS:
      return { ...state };
    case actionType.CS_GET_SETTINGS_SUCCESS:
      const settings = action.payload.data.content;
      const audienceCountries =
        settings?.enabledProvidersAndCountries?.audienceCountries ?? null;
      const instagramAudienceCountries =
        audienceCountries?.[CONSTS.socialProvider.instagram] ?? [];

      const columns = reorderColumns(
        settings?.creatorSearchColumns ?? [],
        getColumnOrderFromStorage(),
      );

      return {
        ...state,
        audienceCountries: instagramAudienceCountries,
        audienceLanguages: settings.audienceLanguages ?? [],
        creatorTags: settings.creatorTags,
        influencerCountries:
          settings.enabledProvidersAndCountries.influencerCountries,
        influencerCountriesByProvider:
          settings.enabledProvidersAndCountries.influencerCountriesByProvider,
        interests: settings.audienceInterests,
        audienceBrandAffinity: settings.brandAffinity,
        keywords: settings.aiTags,
        hasAccessToInfluencerInsightsToolVelocityIndex:
          settings.isVelocityEnabled,
        unifiedCategories: settings.unifiedCategories,
        columns,
        hiddenColumns: computeHiddenColumns(columns, state.columnOverrides),
        sortOptions: settings.creatorSearchSortOptions,
      };

    case actionType.CS_SET_AUDIENCE_DATA: {
      const {
        influencerSearchResults,
        similarProfiles,
        pendingUsers,
        isResultsLimitReached,
        ...rest
      } = action.payload.data;

      return {
        ...state,
        pendingUsers,
        searchAudienceResult: influencerSearchResults,
        similarProfiles: similarProfiles || [],
        searchAudienceInfo: rest,
        lastFetchWasEmpty: false,
        isResultsLimitReached: isResultsLimitReached || false,
      };
    }

    case actionType.CS_CLEAR_AUDIENCE_DATA:
      return {
        ...state,
        pendingUsers: null,
        similarProfiles: [],
        searchAudienceResult: [],
        searchAudienceInfo: {},
        lastFetchWasEmpty: false,
        isResultsLimitReached: false,
      };

    case actionType.CS_SET_AUDIENCE_LOADING:
      return {
        ...state,
        audienceLoading: action.payload.isLoading,
      };

    case actionType.CS_POST_LOAD_MORE_SEARCH_RESULTS_SUCCESS: {
      const {
        influencerSearchResults,
        similarProfiles,
        pendingUsers,
        isResultsLimitReached,
        ...rest
      } = action.payload.data.content;

      return {
        ...state,
        pendingUsers,
        searchAudienceResult: [
          ...state.searchAudienceResult,
          ...influencerSearchResults,
        ],
        similarProfiles: similarProfiles
          ? [...state.similarProfiles, ...similarProfiles]
          : state.similarProfiles,
        searchAudienceInfo: rest,
        lastFetchWasEmpty: influencerSearchResults.length === 0,
        isResultsLimitReached: isResultsLimitReached || false,
      };
    }

    case actionType.CS_POST_REFRESH_AUDIENCE_SUCCESS:
      if (action.payload.data.content.pendingUsers !== state.pendingUsers) {
        const timeStampedAudienceResult =
          action.payload.data.content.influencerSearchResults.length > 0
            ? [
                {
                  ...action.payload.data.content.influencerSearchResults[0],
                  profilePictureUrl:
                    action.payload.data.content.influencerSearchResults[0]
                      .profilePictureUrl &&
                    `${
                      action.payload.data.content.influencerSearchResults[0]
                        .profilePictureUrl
                    }&timestamp=${new Date().getTime()}`,
                },
              ]
            : [];

        return {
          ...state,
          pendingUsers:
            action.payload.data.content.pendingUsers === null
              ? state.pendingUsers
              : action.payload.data.content.pendingUsers,
          searchAudienceResult: timeStampedAudienceResult,
          searchAudienceInfo: {
            totalAvgErPercent: action.payload.data.content.totalAvgErPercent,
            totalAvgVelocityIndex:
              action.payload.data.content.totalAvgVelocityIndex,
            totalPotentialReach:
              action.payload.data.content.totalPotentialReach,
            totalResults: action.payload.data.content.totalResults,
          },
          audienceLoading: false,
        };
      }
      return state;

    case actionType.CS_CHANGE_SEARCH_AUDIENCE_FILTERS: {
      const { searchData } = action.payload;

      return {
        ...state,
        searchAudienceFilters: searchData,
      };
    }

    case actionType.CS_SELECT_INFLUENCER: {
      const { userId, profileUrl } = action.payload;
      const { selectedInfluencersOnResults, selectedInfluencersOnResultsUrls } =
        state;

      const newSelectedInfluencers =
        selectedInfluencersOnResults.indexOf(userId) > -1
          ? selectedInfluencersOnResults.filter((itemId) => itemId !== userId)
          : [...selectedInfluencersOnResults, userId];

      const newSelectedInfluencersUrls =
        selectedInfluencersOnResultsUrls.indexOf(profileUrl) > -1
          ? selectedInfluencersOnResultsUrls.filter(
              (itemUrl) => itemUrl !== profileUrl,
            )
          : [...selectedInfluencersOnResultsUrls, profileUrl];

      return {
        ...state,
        selectedInfluencersOnResults: newSelectedInfluencers,
        selectedInfluencersOnResultsUrls: newSelectedInfluencersUrls,
      };
    }

    case actionType.CS_SELECT_MULTIPLE_INFLUENCER: {
      const {
        userIds: multipleUserIds,
        profileUrls: multipleUserUrls,
        value: multipleValue,
      } = action.payload;

      const {
        selectedInfluencersOnResults: selectedOnResults,
        selectedInfluencersOnResultsUrls: selectedOnResultsUrls,
      } = state;

      const addMany = (current, incoming) => [
        ...new Set([...current, ...incoming]),
      ];

      const removeMany = (current, incoming) =>
        current.filter((item) => !incoming.includes(item));

      const operation = multipleValue ? addMany : removeMany;

      return {
        ...state,
        selectedInfluencersOnResults: operation(
          selectedOnResults,
          multipleUserIds,
        ),
        selectedInfluencersOnResultsUrls: operation(
          selectedOnResultsUrls,
          multipleUserUrls,
        ),
      };
    }

    case actionType.CS_SET_IS_INCORRECT_PROVIDER_ERROR:
      return {
        ...state,
        isIncorrectProviderError: action.payload,
      };

    case actionType.CS_ADD_TO_AUDIENCES_SUCCESS: {
      showToast(
        "success",
        <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
        <IDHFormattedMessage
          id="ws_creators_added_to_list"
          defaultMessage="Creators successfully added to list"
        />,
      );

      return {
        ...state,
        selectedInfluencersOnResults: [],
        selectedInfluencersOnResultsUrls: [],
      };
    }

    case actionType.CS_ADD_TO_AUDIENCES_FAIL: {
      showToast(
        "error",
        <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
        <IDHFormattedMessage
          id={action?.error?.response?.data?.content?.errors[0]}
        />,
      );

      return state;
    }

    case actionType.CS_CLEAR_ALL_SELECTED_USERS:
      return {
        ...state,
        selectedInfluencersOnResults: [],
        selectedInfluencersOnResultsUrls: [],
      };

    case actionType.CS_GET_VELOCITY_SUMMARY_DATA_SUCCESS:
      return {
        ...state,
        velocitySummary: { ...action.payload.data.content },
      };

    case actionType.CS_CLEAR_VELOCITY_SUMMARY_DATA:
      return {
        ...state,
        velocitySummary: {},
      };

    case actionType.CS_SET_IS_AUDIENCE_DATA_ERROR:
      return {
        ...state,
        isAudienceDataError: action.payload.isAudienceDataError,
      };

    case actionType.CS_SET_IS_ANALYZING_BRIEF:
      return {
        ...state,
        isAnalyzingBrief: action.payload.isAnalyzingBrief,
      };

    case actionType.CS_TOGGLE_COLUMN_VISIBILITY: {
      const { key } = action.payload;
      const isHidden = state.hiddenColumns.includes(key);

      const columnOverrides = {
        ...state.columnOverrides,
        [key]: isHidden,
      };

      saveColumnOverridesToStorage(columnOverrides);

      return {
        ...state,
        columnOverrides,
        hiddenColumns: computeHiddenColumns(state.columns, columnOverrides),
      };
    }

    case actionType.CS_SET_COLUMN_ORDER: {
      const { order } = action.payload;

      saveColumnOrderToStorage(order);

      return {
        ...state,
        columns: reorderColumns(state.columns, order),
      };
    }

    case actionType.CS_MARK_CREATOR_AS_VIEWED: {
      const { hashUserId } = action.payload;
      const markViewed = (list) =>
        list.map((influencer) =>
          influencer.userId === hashUserId
            ? { ...influencer, isViewed: true }
            : influencer,
        );

      return {
        ...state,
        searchAudienceResult: markViewed(state.searchAudienceResult),
        similarProfiles: markViewed(state.similarProfiles),
      };
    }

    case actionType.CS_MARK_CREATORS_IN_CAMPAIGN: {
      const { hashUserIds } = action.payload;
      const idSet = new Set(hashUserIds);
      const markInCampaign = (list) =>
        list.map((influencer) =>
          idSet.has(influencer.userId)
            ? { ...influencer, isInCampaign: true }
            : influencer,
        );

      return {
        ...state,
        searchAudienceResult: markInCampaign(state.searchAudienceResult),
        similarProfiles: markInCampaign(state.similarProfiles),
      };
    }

    default:
      return state;
  }
}
